import React, { useEffect, useMemo, useRef } from 'react'
import { Platform, StyleSheet } from 'react-native'
import { useTheme } from 'react-native-paper'
import { useIsFocused } from '@react-navigation/core'
import Color from 'color'
import PropTypes from 'prop-types'
import { helpers } from '@linqpal/common-frontend'
import { webSpecificStyle } from '@linqpal/common-frontend/src/helpers'
import { useThemeStyle } from '@linqpal/common-frontend/src/hooks'
import { InputText } from '@linqpal/common-frontend/src/ui'
import { useResponsive } from '../../utils/hooks'

const { composeStyle } = helpers

const useCommonTextInputStyles = () => useThemeStyle((theme) => ({
  countryCodeInput: {
    maxWidth: 36,
    marginRight: 8
  },
  inputOuterStyle: {
    borderColor: '#9BAEBC',
    height: 30
  },
  input: {
    borderColor: theme.colors.tertiaryLight,
    borderWidth: StyleSheet.hairlineWidth,
    backgroundColor: 'transparent',
    color: theme.colors.primary,
    flex: 1,
    fontSize: 18,
    fontWeight: '400',
    lineHeight: 24,
    minWidth: 200,
    marginTop: 0,
    width: '100%'
  }
}))

const propValuesMap = {
  autoCompleteType: {
    'off': 'off',
    'username': 'username',
    'password': 'password',
    'new-password': 'password',
    'current-password': 'password',
    'email': 'email',
    'name': 'name',
    'nickname': 'name',
    'tel': 'tel',
    'tel-national': 'tel',
    'tel-local': 'tel',
    'street-address': 'street-address',
    'postal-code': 'postal-code',
    'cc-number': 'cc-number',
    'cc-csc': 'cc-csc',
    'cc-exp': 'cc-exp',
    'cc-exp-month': 'cc-exp-month',
    'cc-exp-year': 'cc-exp-year'
  },
  textContentType: {
    'off': 'none',
    'username': 'username',
    'password': 'password',
    'tel': 'telephoneNumber',
    'url': 'URL',
    'address-level2': 'addressCity',
    'address-level1': 'addressCityAndState',
    'addressState': 'addressState',
    'country-name': 'countryName',
    'cc-number': 'creditCardNumber',
    'email': 'emailAddress',
    'family-name': 'familyName',
    'street-address': 'fullStreetAddress',
    'given-name': 'givenName',
    'additional-name': 'middleName',
    'name': 'name',
    'honorific-prefix': 'namePrefix',
    'honorific-suffix': 'nameSuffix',
    'nickname': 'nickname',
    'organization-title': 'organizationName',
    'postal-code': 'postalCode',
    'address-line1': 'streetAddressLine1',
    'address-line2': 'streetAddressLine2',
    'address-level4': 'sublocality'
  }
}

function useAutoComplete (props) {
  const { autoComplete, ...rest } = props
  const newProps = useMemo(() => {
    if (typeof autoComplete !== 'string') return { autoComplete }

    switch (Platform.OS) {
      case 'android':
      case 'ios':
        const osSpecificProperty = Platform.OS === 'android' ? 'autoCompleteType' : 'textContentType',
          newValue = propValuesMap[osSpecificProperty][autoComplete]
        if (typeof newValue === 'string') {
          return { [osSpecificProperty]: newValue }
        }
      default:
        return { autoComplete }
    }
  }, [autoComplete])
  return { ...newProps, ...rest }
}

const Input = (props) => {
  const {
      inputRef, style, autoFocus, inputOuterStyle,
      innerBorderStyle, ...rest
    } = useAutoComplete(props),
    styles = useCommonTextInputStyles()

  if (inputRef) rest.ref = inputRef
  const { primary } = useTheme().colors
  if (!rest.placeholderTextColor) {
    rest.placeholderTextColor = Color(primary).alpha(0.2).rgb().string()
  }
  const inputStyle = useMemo(() => {
    return composeStyle(styles.input, webSpecificStyle({
      caretColor: primary,
      outline: 'none'
    }), style)
  }, [style])

  const isFocused = useIsFocused()
  const ref = useRef()

  useEffect(() => {
    if (ref.current && autoFocus && isFocused) {
      ref.current.focus()
    }
  }, [isFocused])

  const { sm } = useResponsive()

  return (
    <InputText ref={ref}
               {...rest}
               style={inputStyle}
               innerBorderStyle={composeStyle({
                 borderWidth: 0,
                 paddingTop: 16,
                 padding: 16,
                 width: sm ? 360 : 300
               }, innerBorderStyle)}
               inputOuterStyle={composeStyle(styles.inputOuterStyle, inputOuterStyle)}
               underlineColor="transparent"/>
  )
}

const CommonTextInput = React.forwardRef((props, ref) => <Input inputRef={ref}  {...props} />)

CommonTextInput.propTypes = {
  value: PropTypes.string,
  onChangeText: PropTypes.func,
  autoFocus: PropTypes.bool,
  autoComplete: PropTypes.string,
  editable: PropTypes.bool,
  keyboardType: PropTypes.string,
  returnKeyType: PropTypes.string,
  onSubmitEditing: PropTypes.func,
  placeholder: PropTypes.string,
  placeholderTextColor: PropTypes.string,
  secureTextEntry: PropTypes.bool,
  style: PropTypes.any
}

// noinspection JSValidateTypes
CommonTextInput.defaultProps = {
  returnKeyType: 'go'
}

export default CommonTextInput
